// src/post/infrastructure/ui-services/postUiService.ts

import type { Post, PostsResponse } from "~/src/post/domain/models/Post";
import type { NitroFetchOptions } from "nitropack";
import { useWebContext } from "~/app/composables/webContext";
import { useCompetitionStore } from "~/app/stores/competitionStore";
import { $FEATURED_ITEMS } from "~/src/post/domain/service/PostService";
import { getWebContextCmsCategories } from "~/src/region/datasources/regionsData";

interface PostUiService {
  getFeaturedPosts: (lang: string, checkDate?: boolean) => Promise<PostsResponse | undefined>;
  getPosts: (opts: NitroFetchOptions<any>) => Promise<PostsResponse | undefined>;
  getPost: (slug: string, lang: string) => Promise<Post | undefined>;
  isExternal: (categoryIds: number[]) => boolean;
  parseString: (title: string) => string;
  localeHasNews: (lang?: string) => Promise<boolean>;
  getRegionAvailableNewsLocales: () => Promise<string[] | undefined>;
  getAllRegionsAvailableNewsLocales: () => Promise<Record<string, string[]> | undefined>;
  // checkRepeatedPostInRegions: (
  //   postCategories: number[],
  //   webContext: WebContext
  // ) => { repeated: boolean; cmsKey: string };
}

const getAllRegionsAvailableNewsLocales = async (): Promise<Record<string, string[]> | undefined> => {
  const { webContext } = getRequiredContextData();
  return await $fetch(`/api/cms/regions-available-locales?ctx=${webContext}`);
};

const getRegionAvailableNewsLocales = async (): Promise<string[] | undefined> => {
  const { currentRegionId } = getRequiredContextData();
  const regionsLocalesData = await getAllRegionsAvailableNewsLocales();
  if (!!regionsLocalesData && currentRegionId) {
    return regionsLocalesData[currentRegionId] || undefined;
  } else {
    return undefined;
  }
};

const localeHasNews = async (lang?: string): Promise<boolean> => {
  const { locale } = getRequiredContextData();
  const regionNewsAvailable = await getRegionAvailableNewsLocales();
  if (!!regionNewsAvailable) {
    if (!lang) {
      return regionNewsAvailable.includes(locale);
    } else {
      return regionNewsAvailable.includes(lang);
    }
  } else {
    return false;
  }
};

const getFeaturedPosts = async (lang: string, checkDate?: boolean): Promise<PostsResponse | undefined> => {
  const { webContext, locale, currentRegionId } = getRequiredContextData();
  const newsCategories = getWebContextCmsCategories(webContext);
  if (!currentRegionId || !webContext || !locale || !lang) {
    console.error("Region, webContext and locale required.");
    return undefined;
  }

  const generalId = !!newsCategories[currentRegionId] ? newsCategories[currentRegionId]?.general : undefined;

  if (generalId) {
    const opts: NitroFetchOptions<any> = {
      params: {
        per_page: $FEATURED_ITEMS,
        categories: `${generalId}`,
        lang: lang || locale,
        check_date: checkDate,
      },
    };

    return await fetchPosts(opts);
  } else {
    console.error("General category not found");
    return undefined;
  }
};

const getPosts = async (newOpts?: NitroFetchOptions<any>): Promise<PostsResponse | undefined> => {
  const { webContext, locale, currentRegionId } = getRequiredContextData();
  const newsCategories = getWebContextCmsCategories(webContext);

  if (!currentRegionId || !webContext || !locale) {
    console.error("Region, webContext and locale required.");
    return undefined;
  }
  const webContextCategories = !!newsCategories[currentRegionId]
    ? newsCategories[currentRegionId]
    : undefined;

  if (webContextCategories) {
    const allCategories = Object.values(webContextCategories);
    const generalId = webContextCategories?.general;

    const opts: NitroFetchOptions<any> = {
      params: {
        categories: `${generalId}`,
      },
    };

    if (newOpts) {
      opts.params = { ...opts.params, ...newOpts.params };
    }

    if (opts?.params?.categories) {
      //check if category exists in predefined categories
      const matchedCategories: Number[] = opts.params.categories
        .split(",")
        .filter((category: string) => {
          return allCategories.includes(Number(category));
        })
        .join(",");

      if (matchedCategories.length) {
        opts.params.categories = matchedCategories;
      }
    }

    return await fetchPosts(opts);
  } else {
    console.error("General category not found");
    return undefined;
  }
};

const getPost = async (slug: string, lang: string): Promise<Post | undefined> => {
  const opts = {
    params: {
      per_page: 1,
      slug,
      lang,
    },
  };
  const response = await fetchPosts(opts);
  if (response) {
    return response.data[0];
  } else {
    throw createError({ statusCode: 404 });
  }
};

const isExternal = (categories: number[]): boolean => {
  const { webContext, locale, currentRegionId } = getRequiredContextData();
  const newsCategories = getWebContextCmsCategories(webContext);

  if (!currentRegionId || !webContext || !locale) {
    console.error("Region, webContext and locale required to know if is external.");
    return false;
  }

  const externalId: number | undefined = !!newsCategories[currentRegionId]
    ? newsCategories[currentRegionId].external
    : undefined;

  if (externalId) {
    return categories.includes(externalId);
  } else {
    console.error("externalId is undefined.");
    return false;
  }
};

const parseString = (title: string): string => {
  if (!title) return "";

  const parser = new DOMParser();
  const dom = parser.parseFromString(`${title}`, "text/html");
  return dom.body.textContent || "";
};

const postUiService = (): PostUiService => ({
  getFeaturedPosts,
  getPosts,
  getPost,
  isExternal,
  parseString,
  localeHasNews,
  getRegionAvailableNewsLocales,
  getAllRegionsAvailableNewsLocales,
  // checkRepeatedPostInRegions,
});

export default postUiService;

function getCurrentRegionId(): number | undefined {
  const { region } = useCompetitionStore();
  return region?.id;
}

async function fetchPosts(opts: NitroFetchOptions<any>): Promise<PostsResponse | undefined> {
  const regionId = getCurrentRegionId();
  if (!regionId) {
    console.error("Invalid regionId.");
    return undefined;
  }

  try {
    const posts = await $fetch<PostsResponse>(`/api/cms/${regionId}/news`, opts);
    if (posts.data.length) {
      return posts;
    } else {
      console.error("Post/s not found in CMS.");
    }
  } catch (e) {
    console.error("Error fetching posts.");
  }
}

function getRequiredContextData() {
  const { webContext } = useWebContext();
  const locale = useCookie("lang");
  const currentRegionId = getCurrentRegionId();

  return { webContext, locale: locale.value || "es", currentRegionId };
}

// function checkRepeatedPostInRegions(
//   postCategories: number[],
//   webContext: WebContext
// ): { repeated: boolean; cmsKey: string } {
//   let foundObjects = new Set();
//   let firstObject = null;
//
//   for (let cmsKey in newsCategories) {
//     let cms = newsCategories[cmsKey];
//     for (let lang in cms) {
//       if (cms[lang][webContext]) {
//         for (let category in cms[lang][webContext]) {
//           if (postCategories.includes(cms[lang][webContext][category])) {
//             foundObjects.add(cmsKey);
//
//             if (!firstObject) {
//               firstObject = cmsKey;
//             }
//           }
//         }
//       }
//     }
//   }
//
//   return { repeated: foundObjects.size > 1, cmsKey: firstObject };
// }
